import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiInbox,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification from "../../components/common/notification/Notification";
import Spinner from "../../components/common/spinner/Spinner";
import { usePromotionApiClient } from "./services/usePromotionApiClient";
import { createPromotionService } from "./services/promotionService";
import { PROMOTION_TABS, PROMOTION_DEFAULT_PAGE_SIZE } from "./constants/promotionEndpoints";
import PromotionPageHeader from "./components/PromotionPageHeader";
import PromotionFilterToolbar from "./components/PromotionFilterToolbar";
import PromotionCard from "./components/PromotionCard";
import PromotionTableView from "./components/PromotionTableView";
import PromotionCreateModal from "./components/PromotionCreateModal";
import PromotionDeleteModal from "./components/PromotionDeleteModal";
import "./PromotionsPage.css";

const PromotionsPage = ({ routeBase = "/promotions" }) => {
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId =
    authUser?.user?.id || authUser?.id || authUser?.userDetails?.id || 1;
  const currentUserName =
    [authUser?.user?.first_name, authUser?.user?.last_name].filter(Boolean).join(" ") ||
    authUser?.user?.name ||
    "User";

  const userType =
    authUser?.user?.userType?.name ||
    authUser?.user?.userType ||
    authUser?.userType ||
    "CUSTOMER";
  const isPartner = String(userType).toUpperCase().includes("PARTNER");
  const isAdmin =
    String(userType).toUpperCase().includes("ADMIN") ||
    String(userType).toUpperCase().includes("ALL_HOME_DESIRE");

  const apiClient = usePromotionApiClient();
  const promotionService = useMemo(
    () => createPromotionService(apiClient),
    [apiClient]
  );

  // Active Tab
  const [activeTab, setActiveTab] = useState(
    isPartner ? PROMOTION_TABS.PREFERRED : PROMOTION_TABS.ALL
  );

  // List data state
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);

  // Filters state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  // Metadata for filter dropdowns
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // View mode: grid vs table
  const [viewMode, setViewMode] = useState("grid");

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState(null);

  // Load metadata filters once
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [typesData, statesData] = await Promise.all([
          promotionService.fetchPropertyTypes(),
          promotionService.fetchStates(1),
        ]);
        setPropertyTypes(typesData || []);
        setStates(statesData || []);
      } catch (err) {
        console.error("Failed to load metadata:", err);
      }
    };
    fetchMetadata();
  }, [promotionService]);

  // When state changes in filter, load cities
  const handleStateFilterChange = async (stateName) => {
    setSelectedState(stateName);
    setSelectedCity("");
    if (!stateName) {
      setCities([]);
      return;
    }
    const stObj = states.find((s) => s.name === stateName);
    if (stObj?.id) {
      try {
        const citiesData = await promotionService.fetchCities(stObj.id);
        setCities(citiesData || []);
      } catch (err) {
        console.error("Failed to load cities for state:", err);
      }
    }
  };

  // Main fetch promotions list logic implementing APIs 1, 2, and 3
  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      let data = null;

      if (activeTab === PROMOTION_TABS.MY) {
        // API 3: List My / Specific Partner's Promotions
        data = await promotionService.listMyPreferred({
          user_id: currentUserId,
          page_number: currentPage,
          page_size: PROMOTION_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
        });
      } else if (
        activeTab === PROMOTION_TABS.PREFERRED ||
        selectedType ||
        selectedState ||
        selectedCity ||
        searchTerm
      ) {
        // API 2: List Promotions with Filters / Preferred (Partner View)
        data = await promotionService.listPreferred({
          type: selectedType,
          state: selectedState,
          city: selectedCity,
          keyboard_search: searchTerm,
          page_number: currentPage,
          page_size: PROMOTION_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
          is_partner: true,
        });
      } else {
        // API 1: List Promotions (Customer / General View)
        data = await promotionService.listGeneral({
          page_number: currentPage,
          page_size: PROMOTION_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
        });
      }

      const list =
        data?.promotions ||
        data?.data ||
        data?.propertyAssets ||
        (Array.isArray(data) ? data : []);

      setPromotions(list);
      setTotalPages(data?.totalPages || data?.total_pages || 1);
      if (typeof data?.totalCount === "number") {
        setTotalCount(data.totalCount);
      } else if (typeof data?.total === "number") {
        setTotalCount(data.total);
      } else {
        setTotalCount(list.length);
      }
    } catch (error) {
      console.warn("Error fetching promotions:", error);
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    currentPage,
    currentUserId,
    searchTerm,
    selectedCity,
    selectedState,
    selectedType,
    promotionService,
  ]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // Handle Tab Switch
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedType("");
    setSelectedState("");
    setSelectedCity("");
    setCurrentPage(1);
  };

  // Actions - Dedicated Full-Page Navigation
  const handleViewDetails = (promo) => {
    const cleanBase = routeBase.replace(/\/+$/, "");
    navigate(`${cleanBase}/${promo.id}`);
  };

  const handleOpenComments = (promo) => {
    const cleanBase = routeBase.replace(/\/+$/, "");
    navigate(`${cleanBase}/${promo.id}/discussion`);
  };

  const handleDeletePrompt = (promo) => {
    setPromotionToDelete(promo);
  };

  const handlePromotionDeleted = (deletedId) => {
    setPromotions((prev) => prev.filter((p) => p.id !== deletedId));
  };

  const canCreate = isPartner || isAdmin;

  return (
    <div className="promotions-module-page">
      <Notification />

      {/* Page Header */}
      <PromotionPageHeader
        title="Promotions"
        description="Explore exclusive property promotions, partner campaigns, luxury residential developments, and promotional deals."
        onOpenCreate={() => setShowCreateModal(true)}
        canCreate={canCreate}
        totalCount={totalCount}
      />

      {/* Filter Toolbar with Tabs & Search */}
      <PromotionFilterToolbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={fetchPromotions}
        selectedType={selectedType}
        onTypeChange={(val) => {
          setSelectedType(val);
          setCurrentPage(1);
        }}
        propertyTypes={propertyTypes}
        selectedState={selectedState}
        onStateChange={handleStateFilterChange}
        states={states}
        selectedCity={selectedCity}
        onCityChange={(val) => {
          setSelectedCity(val);
          setCurrentPage(1);
        }}
        cities={cities}
        onResetFilters={handleResetFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isPartner={isPartner}
        showMyTab={Boolean(currentUserId)}
      />

      {/* Main Promotions Content Area */}
      {loading ? (
        <div className="promotions-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading promotions...</p>
        </div>
      ) : promotions.length === 0 ? (
        <div className="promotions-empty-state">
          <div className="promotions-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="promotions-empty-state__title">No Promotions Found</h3>
          <p className="promotions-empty-state__text">
            {searchTerm || selectedType || selectedState || selectedCity
              ? "Try adjusting your search criteria or reset filters to see all available promotions."
              : activeTab === PROMOTION_TABS.MY
              ? "You haven't created any promotions yet. Click 'Create Promotion' to publish your first campaign."
              : "There are currently no active promotions listed in this category."}
          </p>
          {(searchTerm || selectedType || selectedState || selectedCity) && (
            <button
              type="button"
              className="btn btn-outline-primary mt-2"
              onClick={handleResetFilters}
            >
              Reset All Filters
            </button>
          )}
          {canCreate && activeTab === PROMOTION_TABS.MY && (
            <button
              type="button"
              className="btn btn-primary mt-3 d-inline-flex align-items-center gap-2"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus /> Create Promotion
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="promotions-grid">
          {promotions.map((promo) => {
            const isOwner =
              currentUserId &&
              Number(promo.partner?.id || promo.user?.id) === Number(currentUserId);
            const canDelete = isOwner || isAdmin;

            return (
              <PromotionCard
                key={promo.id}
                promotion={promo}
                onViewDetails={handleViewDetails}
                onOpenComments={handleOpenComments}
                onDelete={handleDeletePrompt}
                canDelete={canDelete}
              />
            );
          })}
        </div>
      ) : (
        <PromotionTableView
          promotions={promotions}
          onViewDetails={handleViewDetails}
          onOpenComments={handleOpenComments}
          onDelete={handleDeletePrompt}
          canDelete={isAdmin || isPartner}
        />
      )}

      {/* Pagination Bar */}
      {totalPages > 1 && !loading && (
        <div className="promotions-pagination-bar">
          <button
            type="button"
            className="promotions-page-nav-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>

          <span className="promotions-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            type="button"
            className="promotions-page-nav-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Create Promotion Modal (APIs 5, 7, 8, 9) */}
      <PromotionCreateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        promotionService={promotionService}
        onPromotionCreated={fetchPromotions}
      />

      {/* Delete Confirmation Modal (API 6) */}
      <PromotionDeleteModal
        show={Boolean(promotionToDelete)}
        onHide={() => setPromotionToDelete(null)}
        promotion={promotionToDelete}
        promotionService={promotionService}
        onPromotionDeleted={handlePromotionDeleted}
      />
    </div>
  );
};

export default PromotionsPage;
